<script setup lang="ts">
    import { useScrollLock } from '@vueuse/core';
    import { useDismiss, useFocusTrap } from '@/shared/composables';
    import { useModalContext } from './context';

    const props = withDefaults(
        defineProps<{
            /** The panel's maximum width: `sm` 384px, `md` 512px, `lg` 768px, `xl` 896px; `full` fills the screen less a 16px margin */
            size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
            /** Escape closes the modal. Turn it off when closing loses data that cannot come back */
            closeOnEscape?: boolean;
            /** A press on the overlay closes the modal */
            closeOnClickOutside?: boolean;
        }>(),
        { size: 'md', closeOnEscape: true, closeOnClickOutside: true },
    );

    defineOptions({ inheritAttrs: false });

    defineSlots<{
        /** The header, body and footer. `close` closes the modal */
        default(props: { close: () => void }): unknown;
    }>();

    const { open, contentId, titleId, descriptionId, hasTitle, hasDescription, setOpen, close } = useModalContext('ModalContent');

    const panel = useTemplateRef('panel');

    /* A ModalHeader names and describes the dialog unless the consumer does. */
    const attrs = useAttrs();
    const labelledBy = computed(() => (attrs['aria-label'] || attrs['aria-labelledby'] || !hasTitle.value ? attrs['aria-labelledby'] : titleId));
    const describedBy = computed(() => attrs['aria-describedby'] ?? (hasDescription.value ? descriptionId : undefined));

    /* Vue gives the parent's scope id only to a root element, and the root here is the Teleport: pass it on, so the parent's scoped classes reach the panel. */
    const parentScopeId = getCurrentInstance()?.vnode.scopeId;
    const scopeAttrs = parentScopeId ? { [parentScopeId]: '' } : {};

    useDismiss({
        open,
        elements: () => [panel.value],
        // The trap keeps focus inside, and the overlay blocks the page
        focusOutside: false,
        onDismiss: (reason) => {
            if (reason === 'escape' ? props.closeOnEscape : props.closeOnClickOutside) setOpen(false);
        },
    });

    useFocusTrap({ active: open, container: panel });

    /* The page behind does not scroll while the modal is open. */
    const scrollLocked = useScrollLock(typeof document === 'undefined' ? null : document.body);
    watch(open, (isOpen) => (scrollLocked.value = isOpen), { immediate: true });
    onBeforeUnmount(() => (scrollLocked.value = false));
</script>

<template>
    <Teleport to="body">
        <Transition name="ui-modal-overlay">
            <div
                v-if="open"
                class="ui-modal-overlay"
                data-slot="modal-overlay"
                aria-hidden="true"
            />
        </Transition>
        <!-- Removed while closed, after the fade-out -->
        <Transition name="ui-modal">
            <div
                v-if="open"
                v-bind="{ ...scopeAttrs, ...$attrs }"
                :id="contentId"
                ref="panel"
                role="dialog"
                aria-modal="true"
                tabindex="-1"
                data-slot="modal-content"
                :class="['ui-modal', `ui-modal--${size}`]"
                :aria-labelledby="labelledBy"
                :aria-describedby="describedBy"
                :data-state="open ? 'open' : 'closed'"
                :data-size="size"
            >
                <slot :close="close" />
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
    /* Below `utilities` so consumers' Tailwind classes (a width) still override these defaults, above Bulma's `base`. */
    @layer components {
        .ui-modal-overlay {
            position: fixed;
            inset: 0;
            z-index: 50;
            background-color: rgb(0 0 0 / 0.8);
        }

        /* Centred with `translate`, so the enter and leave animations are free to use `scale`. */
        .ui-modal {
            position: fixed;
            top: 50%;
            left: 50%;
            z-index: 50;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            width: calc(100% - 2rem);
            max-height: calc(100% - 2rem);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-lg);
            background-color: var(--color-background-primary);
            color: var(--color-content-primary);
            box-shadow:
                0 10px 15px -3px rgb(0 0 0 / 0.1),
                0 4px 6px -4px rgb(0 0 0 / 0.1);
            outline: none;
            translate: -50% -50%;
        }

        .ui-modal--sm {
            max-width: 24rem;
        }

        .ui-modal--md {
            max-width: 32rem;
        }

        .ui-modal--lg {
            max-width: 48rem;
        }

        .ui-modal--xl {
            max-width: 56rem;
        }

        .ui-modal--full {
            height: calc(100% - 2rem);
        }

        /* Fades and zooms from 95% over 200ms. */
        .ui-modal-overlay-enter-active,
        .ui-modal-overlay-leave-active,
        .ui-modal-enter-active,
        .ui-modal-leave-active {
            transition-duration: 200ms;
            transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }

        .ui-modal-overlay-enter-active,
        .ui-modal-overlay-leave-active {
            transition-property: opacity;
        }

        .ui-modal-enter-active,
        .ui-modal-leave-active {
            transition-property: opacity, scale;
        }

        .ui-modal-overlay-enter-from,
        .ui-modal-overlay-leave-to {
            opacity: 0;
        }

        .ui-modal-enter-from,
        .ui-modal-leave-to {
            opacity: 0;
            scale: 0.95;
        }

        @media (prefers-reduced-motion: reduce) {
            .ui-modal-overlay-enter-active,
            .ui-modal-overlay-leave-active,
            .ui-modal-enter-active,
            .ui-modal-leave-active {
                transition-duration: 0s;
            }
        }
    }
</style>
